import { useEffect, useState } from 'react';
import { X, Save } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Asset, Channel, Tag, AssetType, BrandEntity, AssetStatus, PermissionType } from '../../types/assetRegistry';

interface ChannelPermState { channelId: string; permissionType: PermissionType | ''; restrictionNote: string; }
interface AssetFormProps { asset?: Asset | null; onClose: () => void; onSaved: () => void; }

const ASSET_TYPES: AssetType[] = ['image','video','copy','email_template','podcast','training_module'];
const BRAND_ENTITIES: BrandEntity[] = ['carebrave','carepreneur','karavista','carebinge','anand_personal','cba_materials'];
const BRAND_LABELS: Record<BrandEntity, string> = { carebrave:'CareBravo', carepreneur:'Carepreneur', karavista:'Karavista', carebinge:'CareBinge', anand_personal:'Anand', cba_materials:'CBA' };
const STATUSES: AssetStatus[] = ['draft','approved','archived'];

const fieldLabel: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 };
const inputBase: React.CSSProperties = {
  width: '100%', padding: '9px 12px', fontSize: 13, border: '1.5px solid #e5e7eb',
  borderRadius: 8, outline: 'none', background: '#fff', color: '#111827',
  fontFamily: 'inherit', transition: 'border-color 0.15s', boxSizing: 'border-box',
};
const sectionTitle: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 12px' };

export function AssetForm({ asset, onClose, onSaved }: AssetFormProps) {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [title, setTitle] = useState(asset?.title ?? '');
  const [assetType, setAssetType] = useState<AssetType | ''>(asset?.asset_type ?? '');
  const [brandEntity, setBrandEntity] = useState<BrandEntity | ''>(asset?.brand_entity ?? '');
  const [fileUrl, setFileUrl] = useState(asset?.file_url ?? '');
  const [thumbnailUrl, setThumbnailUrl] = useState(asset?.thumbnail_url ?? '');
  const [description, setDescription] = useState(asset?.description ?? '');
  const [status, setStatus] = useState<AssetStatus>(asset?.status ?? 'draft');
  const [kranumId, setKranumId] = useState(asset?.kranum_asset_id ?? '');
  const [channelPerms, setChannelPerms] = useState<ChannelPermState[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  useEffect(() => { fetchMeta(); }, []); // eslint-disable-line

  async function fetchMeta() {
    const [channelsRes, tagsRes] = await Promise.all([
      supabase.from('channels').select('*').order('name'),
      supabase.from('tags').select('*').order('category').order('name'),
    ]);
    const channelList = (channelsRes.data ?? []) as Channel[];
    setChannels(channelList);
    setTags((tagsRes.data ?? []) as Tag[]);
    if (asset) {
      const [permRes, tagRes] = await Promise.all([
        supabase.from('asset_channel_permissions').select('*').eq('asset_id', asset.id),
        supabase.from('asset_tags').select('tag_id').eq('asset_id', asset.id),
      ]);
      setChannelPerms(channelList.map(c => {
        const ex = permRes.data?.find((p: any) => p.channel_id === c.id);
        return { channelId: c.id, permissionType: ex?.permission_type ?? '', restrictionNote: ex?.restriction_note ?? '' };
      }));
      setSelectedTags(tagRes.data?.map((t: any) => t.tag_id) ?? []);
    } else {
      setChannelPerms(channelList.map(c => ({ channelId: c.id, permissionType: '', restrictionNote: '' })));
    }
  }

  function updatePerm(channelId: string, field: 'permissionType' | 'restrictionNote', value: string) {
    setChannelPerms(prev => prev.map(p => p.channelId === channelId ? { ...p, [field]: value } : p));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !fileUrl.trim()) { setError('Title and File URL are required.'); return; }
    setSaving(true); setError('');
    const assetData = {
      title: title.trim(), asset_type: assetType || null, brand_entity: brandEntity || null,
      file_url: fileUrl.trim(), thumbnail_url: thumbnailUrl.trim() || null,
      description: description.trim() || null, status, kranum_asset_id: kranumId.trim() || null,
    };
    let assetId = asset?.id;
    if (asset) {
      const { error: e } = await supabase.from('assets').update(assetData).eq('id', asset.id);
      if (e) { setError(e.message); setSaving(false); return; }
    } else {
      const { data, error: e } = await supabase.from('assets').insert(assetData).select('id').single();
      if (e || !data) { setError(e?.message ?? 'Failed to save'); setSaving(false); return; }
      assetId = (data as any).id;
    }
    if (!assetId) { setSaving(false); return; }
    await supabase.from('asset_channel_permissions').delete().eq('asset_id', assetId);
    const permsToInsert = channelPerms.filter(p => p.permissionType !== '').map(p => ({
      asset_id: assetId!, channel_id: p.channelId, permission_type: p.permissionType as PermissionType,
      restriction_note: p.restrictionNote.trim() || null,
    }));
    if (permsToInsert.length > 0) await supabase.from('asset_channel_permissions').insert(permsToInsert);
    await supabase.from('asset_tags').delete().eq('asset_id', assetId);
    if (selectedTags.length > 0) await supabase.from('asset_tags').insert(selectedTags.map(tagId => ({ asset_id: assetId!, tag_id: tagId })));
    setSaving(false); onSaved();
  }

  const tagsByCategory = tags.reduce<Record<string, Tag[]>>((acc, tag) => {
    if (!acc[tag.category]) acc[tag.category] = [];
    acc[tag.category].push(tag);
    return acc;
  }, {});

  const focusStyle = (e: React.FocusEvent<any>) => (e.target.style.borderColor = '#1B4B8A');
  const blurStyle  = (e: React.FocusEvent<any>) => (e.target.style.borderColor = '#e5e7eb');

  return (
    <div className="asset-registry-scope" style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex' }}>
      <div style={{ flex: 1, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }} onClick={onClose} />
      <div style={{ width: 620, background: '#fff', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 40px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
        <style>{`.ar-panel-header, .ar-panel-header *, .ar-panel-header h2, .ar-panel-header button, .ar-panel-header span { color: #ffffff !important; }`}</style>

        {/* Header */}
        <div className="ar-panel-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', background: '#0f2240', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#ffffff' }}>{asset ? 'Edit Asset' : 'Add New Asset'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', display: 'flex', padding: 4, borderRadius: 6 }}>
            <X size={20} color="#ffffff" />
          </button>
        </div>

        {/* Form body */}
        <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13, padding: '10px 14px', borderRadius: 8, marginBottom: 20 }}>
              {error}
            </div>
          )}

          {/* Basic info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
            <div>
              <label style={fieldLabel}>Title <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Asset title..."
                style={inputBase} onFocus={focusStyle} onBlur={blurStyle} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={fieldLabel}>Asset Type</label>
                <select value={assetType} onChange={e => setAssetType(e.target.value as AssetType | '')}
                  style={{ ...inputBase, cursor: 'pointer' }} onFocus={focusStyle} onBlur={blurStyle}>
                  <option value="">Select type...</option>
                  {ASSET_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                </select>
              </div>
              <div>
                <label style={fieldLabel}>Brand Entity</label>
                <select value={brandEntity} onChange={e => setBrandEntity(e.target.value as BrandEntity | '')}
                  style={{ ...inputBase, cursor: 'pointer' }} onFocus={focusStyle} onBlur={blurStyle}>
                  <option value="">Select brand...</option>
                  {BRAND_ENTITIES.map(b => <option key={b} value={b}>{BRAND_LABELS[b]}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label style={fieldLabel}>File URL <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="text" value={fileUrl} onChange={e => setFileUrl(e.target.value)} placeholder="https://..."
                style={inputBase} onFocus={focusStyle} onBlur={blurStyle} />
            </div>

            <div>
              <label style={fieldLabel}>Thumbnail URL <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
              <input type="text" value={thumbnailUrl} onChange={e => setThumbnailUrl(e.target.value)} placeholder="https://..."
                style={inputBase} onFocus={focusStyle} onBlur={blurStyle} />
            </div>

            <div>
              <label style={fieldLabel}>Description</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="Brief description of this asset..."
                style={{ ...inputBase, resize: 'vertical', minHeight: 80 }} onFocus={focusStyle} onBlur={blurStyle} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={fieldLabel}>Status</label>
                <select value={status} onChange={e => setStatus(e.target.value as AssetStatus)}
                  style={{ ...inputBase, cursor: 'pointer' }} onFocus={focusStyle} onBlur={blurStyle}>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={fieldLabel}>Kranum ID <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
                <input type="text" value={kranumId} onChange={e => setKranumId(e.target.value)} placeholder="Leave blank for now"
                  style={inputBase} onFocus={focusStyle} onBlur={blurStyle} />
              </div>
            </div>
          </div>

          {/* Channel Permissions */}
          <div style={{ marginBottom: 24 }}>
            <p style={sectionTitle}>Channel Permissions</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {channelPerms.map(perm => {
                const channel = channels.find(c => c.id === perm.channelId);
                const showNote = perm.permissionType === 'restricted' || perm.permissionType === 'exclusive';
                return (
                  <div key={perm.channelId} style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px', border: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: '#374151', width: 180, flexShrink: 0 }}>{channel?.name?.replace(/_/g, ' ')}</span>
                      <select value={perm.permissionType} onChange={e => updatePerm(perm.channelId, 'permissionType', e.target.value)}
                        style={{ flex: 1, padding: '7px 10px', fontSize: 12, border: '1.5px solid #e5e7eb', borderRadius: 6, outline: 'none', background: '#fff', color: '#374151', cursor: 'pointer' }}
                        onFocus={focusStyle} onBlur={blurStyle}>
                        <option value="">not set</option>
                        <option value="approved">approved</option>
                        <option value="exclusive">exclusive</option>
                        <option value="restricted">restricted</option>
                      </select>
                    </div>
                    {showNote && (
                      <input type="text" value={perm.restrictionNote} onChange={e => updatePerm(perm.channelId, 'restrictionNote', e.target.value)}
                        placeholder="Restriction or exclusivity note..."
                        style={{ ...inputBase, marginTop: 8, fontSize: 12 }} onFocus={focusStyle} onBlur={blurStyle} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tags */}
          <div>
            <p style={sectionTitle}>Tags</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {Object.entries(tagsByCategory).map(([cat, catTags]) => (
                <div key={cat}>
                  <p style={{ margin: '0 0 6px', fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{cat}</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {catTags.map(tag => (
                      <button key={tag.id} type="button" onClick={() => setSelectedTags(prev => prev.includes(tag.id) ? prev.filter(t => t !== tag.id) : [...prev, tag.id])}
                        style={{
                          padding: '5px 12px', borderRadius: 20, fontSize: 12, border: 'none', cursor: 'pointer', fontWeight: 500,
                          background: selectedTags.includes(tag.id) ? '#1B4B8A' : '#f3f4f6',
                          color: selectedTags.includes(tag.id) ? '#fff' : '#374151',
                          transition: 'all 0.15s',
                        }}>
                        {tag.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 24px', borderTop: '1px solid #e5e7eb', background: '#f9fafb', flexShrink: 0 }}>
          <button type="button" onClick={onClose} style={{ padding: '9px 18px', fontSize: 13, fontWeight: 600, color: '#374151', border: '1.5px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSubmit as any} disabled={saving} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '9px 20px', fontSize: 13, fontWeight: 600,
            color: '#fff', background: saving ? '#93a8c4' : '#1B4B8A', border: 'none', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', transition: 'background 0.15s',
          }}>
            <Save size={14} />{saving ? 'Saving...' : 'Save Asset'}
          </button>
        </div>
      </div>
    </div>
  );
}
